// Начальное состояние формы поля, посчитанное из самого поля.
//
// Раньше это жило в useEffect внутри диалога, и на открытии существующего поля
// форма успевала отрендериться пустой: диалог автофокусит первый редактируемый
// элемент, EditableText по фокусу переходит в режим правки и с этого момента
// перестаёт принимать внешнее значение — а значение приезжало эффектом, то
// есть позже. Итог: у существующего поля Field name и Field key выглядели
// пустыми, и сохранение записывало эту пустоту.
//
// Здесь нет ни React, ни JSX, ни импортов из entities — модуль считается на
// рендере и проверяется обычным юнит-тестом.

export type EditableFieldType =
  | "text"
  | "textarea"
  | "select"
  | "multi_select"
  | "number"
  | "currency"
  | "percent"
  | "rating"
  | "duration"
  | "formula"
  | "rollup"
  | "date"
  | "date_range"
  | "checkbox"
  | "url"
  | "email"
  | "phone"
  | "user_relation"
  | "user_multi_relation"
  | "work_object_relation"
  | "work_object_multi_relation";

export const editableFieldTypes: EditableFieldType[] = [
  "text",
  "textarea",
  "select",
  "multi_select",
  "number",
  "currency",
  "percent",
  "rating",
  "duration",
  "formula",
  "rollup",
  "date",
  "date_range",
  "checkbox",
  "url",
  "email",
  "phone",
  "user_relation",
  "user_multi_relation",
  "work_object_relation",
  "work_object_multi_relation",
];

export type LocationScopeType = "workspace" | "space" | "folder" | "board";

export type RatingScale = 5 | 10 | 100;

export type RelationConfig = {
  inverseCardinality: "many" | "one";
  inverseName?: string;
  isHierarchy: boolean;
};

// Ровно то, что форме нужно от существующего поля. Структурный тип, а не
// импорт из entities — чтобы модуль оставался без зависимостей.
export type FieldFormSource = {
  applicableTypeIds: string[];
  appliesToAllTypes: boolean;
  config?: unknown;
  id: string;
  key: string;
  locations: Array<{ scopeId: string; scopeType: string }>;
  name: string;
  options: Array<{ color: string; id: string; rank: string; value: string }>;
  relationTargetTypeIds?: string[];
  type: string;
  workspaceId?: string | null;
};

export type FieldFormState = {
  applicableTypeIds: string[];
  fieldOptions: Array<{ color: string; id?: string; rank: string; value: string }>;
  formulaExpression: string;
  isKeyDirty: boolean;
  isProgressEnabled: boolean;
  key: string;
  locationKeys: string[];
  name: string;
  numberMax: string;
  numberMin: string;
  numberStep: string;
  personScopeTeamId: string;
  progressMax: string;
  ratingScale: RatingScale;
  relationInverseName: string;
  relationIsHierarchy: boolean;
  relationSingleSource: boolean;
  relationTargetTypeIds: string[];
  rollupOperator: string;
  rollupRelationFieldId: string;
  rollupSourceFieldId: string;
  rollupStatusCategories: string[];
  type: EditableFieldType;
};

export function createFieldFormState(field: FieldFormSource | null, workspaceId: string | null): FieldFormState {
  const effectiveWorkspaceId = field?.workspaceId ?? workspaceId ?? "";
  const relation = readRelationConfig(field?.config);

  return {
    applicableTypeIds: field?.appliesToAllTypes === false ? field.applicableTypeIds : [],
    fieldOptions:
      field?.options.map((option) => ({ color: option.color, id: option.id, rank: option.rank, value: option.value })) ?? [],
    formulaExpression: readFormulaExpression(field?.config),
    // У существующего поля ключ уже выбран и не должен переезжать следом за
    // переименованием.
    isKeyDirty: Boolean(field),
    isProgressEnabled: readProgressFieldConfig(field?.config) !== null,
    key: field?.key ?? "",
    locationKeys: readLocationKeys(field, effectiveWorkspaceId),
    name: field?.name ?? "",
    numberMax: readNumberRangeConfig(field?.config)?.max ?? "",
    numberMin: readNumberRangeConfig(field?.config)?.min ?? "",
    numberStep: readNumberRangeConfig(field?.config)?.step ?? "",
    personScopeTeamId: readPersonScopeTeamId(field?.config),
    progressMax: String(readProgressFieldConfig(field?.config)?.max ?? 100),
    ratingScale: readRatingScale(field?.config),
    relationInverseName: relation?.inverseName ?? "",
    relationIsHierarchy: relation?.isHierarchy === true,
    relationSingleSource: relation?.inverseCardinality === "one",
    relationTargetTypeIds: field?.relationTargetTypeIds ?? [],
    rollupOperator: readRollupOperator(field?.config),
    rollupRelationFieldId: readRollupRelationFieldId(field?.config),
    rollupSourceFieldId: readRollupSourceFieldId(field?.config),
    rollupStatusCategories: readRollupStatusCategories(field?.config),
    type: toEditableType(field?.type) ?? "text",
  };
}

export function locationKey(scopeType: string, scopeId: string) {
  return `${scopeType}:${scopeId}`;
}

export function isConfigurableLocationScope(scopeType: string): scopeType is LocationScopeType {
  return scopeType === "workspace" || scopeType === "space" || scopeType === "folder" || scopeType === "board";
}

function readLocationKeys(field: FieldFormSource | null, effectiveWorkspaceId: string): string[] {
  if (field?.locations.length) {
    return field.locations.flatMap((location) =>
      isConfigurableLocationScope(location.scopeType) ? [locationKey(location.scopeType, location.scopeId)] : [],
    );
  }
  return effectiveWorkspaceId ? [locationKey("workspace", effectiveWorkspaceId)] : [];
}

// Причины, по которым имя или ключ не годятся. Возвращается код, а не готовая
// фраза: текст живёт в словаре и зависит от языка интерфейса.
export type FieldNameError = "tooLong" | "tooShort";
export type FieldKeyError = "duplicate" | "tooLong" | "tooShort";

export function getNameError(name: string): FieldNameError | null {
  const trimmed = name.trim();
  if (trimmed.length < 2) return "tooShort";
  if (trimmed.length > 80) return "tooLong";
  return null;
}

export function getKeyError(
  key: string,
  fields: Array<{ id: string; key: string }>,
  currentFieldId: string | null,
): FieldKeyError | null {
  if (key.length < 2) return "tooShort";
  if (key.length > 48) return "tooLong";
  if (fields.some((field) => field.id !== currentFieldId && field.key === key)) return "duplicate";
  return null;
}

export function toEditableType(type: string | undefined): EditableFieldType | null {
  return editableFieldTypes.includes(type as EditableFieldType) ? (type as EditableFieldType) : null;
}

export function isSelectFieldType(type: EditableFieldType) {
  return type === "select" || type === "multi_select";
}

// Links between work items carry relation semantics; links to people do not.
export function isItemRelationFieldType(type: EditableFieldType) {
  return type === "work_object_relation" || type === "work_object_multi_relation";
}

export function readRatingScale(config: unknown): RatingScale {
  const scale = readNumber(readRecord(config)?.ratingScale);
  return scale === 10 || scale === 100 ? scale : 5;
}

export function readFormulaExpression(config: unknown): string {
  const expression = readRecord(readRecord(config)?.formula)?.expression;
  return typeof expression === "string" ? expression : "";
}

export function readRollupRelationFieldId(config: unknown): string {
  const value = readRecord(readRecord(config)?.rollup)?.relationFieldDefinitionId;
  return typeof value === "string" ? value : "";
}

export function readRollupSourceFieldId(config: unknown): string {
  const value = readRecord(readRecord(config)?.rollup)?.sourceFieldDefinitionId;
  return typeof value === "string" ? value : "";
}

export function readRollupOperator(config: unknown): string {
  const value = readRecord(readRecord(config)?.rollup)?.operator;
  const operator = typeof value === "string" ? value : "count";
  return ["avg", "count", "empty", "filled", "max", "min", "sum"].includes(operator) ? operator : "count";
}

export function readRollupStatusCategories(config: unknown): string[] {
  const raw = readRecord(readRecord(config)?.rollup)?.statusCategories;
  if (!Array.isArray(raw)) return [];
  return raw.filter((entry): entry is string => typeof entry === "string");
}

export function readProgressFieldConfig(config: unknown): { max: number } | null {
  const raw = readRecord(readRecord(config)?.progress);
  if (!raw) return null;
  const max = readNumber(raw.max);
  return { max: max !== null && max > 0 ? max : 100 };
}

export function readRelationConfig(config: unknown): RelationConfig | null {
  const raw = readRecord(readRecord(config)?.relation);
  if (!raw) return null;
  return {
    inverseCardinality: raw.inverseCardinality === "one" ? "one" : "many",
    ...(typeof raw.inverseName === "string" ? { inverseName: raw.inverseName } : {}),
    isHierarchy: raw.isHierarchy === true,
  };
}

export function fieldConfigForSubmit(input: {
  formulaExpression: string;
  progress: { max: number } | null;
  ratingScale: RatingScale;
  relation: RelationConfig;
  rollupOperator: string;
  rollupRelationFieldId: string;
  rollupSourceFieldId: string;
  numberRange?: { max?: number; min?: number; step?: number } | null;
  personScopeTeamId?: string;
  rollupStatusCategories: string[];
  type: EditableFieldType;
}): Record<string, unknown> {
  const progressConfig = input.progress ? { progress: input.progress } : {};
  const rangeConfig = input.numberRange ? { numberRange: input.numberRange } : {};
  if (input.type === "user_relation" || input.type === "user_multi_relation") {
    return input.personScopeTeamId ? { personScope: { teamId: input.personScopeTeamId } } : {};
  }
  if (isItemRelationFieldType(input.type)) {
    return { relation: { ...input.relation, inverseName: input.relation.inverseName || undefined } };
  }
  if (input.type === "rating") {
    return { ratingScale: input.ratingScale };
  }
  if (input.type === "formula") {
    return { formula: { expression: input.formulaExpression }, ...progressConfig };
  }
  if (input.type === "rollup") {
    return {
      rollup: {
        operator: input.rollupOperator,
        relationFieldDefinitionId: input.rollupRelationFieldId,
        sourceFieldDefinitionId: input.rollupOperator === "count" ? null : input.rollupSourceFieldId,
        ...(input.rollupStatusCategories.length > 0 ? { statusCategories: input.rollupStatusCategories } : {}),
      },
      ...progressConfig,
    };
  }
  if (input.type === "number" || input.type === "percent") {
    return { ...progressConfig, ...rangeConfig };
  }
  if (input.type === "currency") {
    return rangeConfig;
  }
  return {};
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Границы читаются строками: пустая строка в форме означает «правила нет», и
// подставлять сюда ноль нельзя — это разные вещи.
function readNumberRangeConfig(config: unknown): { max: string; min: string; step: string } | null {
  const raw = readRecord(readRecord(config)?.numberRange);
  if (!raw) return null;
  const read = (value: unknown) => (Number.isFinite(Number(value)) && value !== null && value !== "" ? String(Number(value)) : "");
  return { max: read(raw.max), min: read(raw.min), step: read(raw.step) };
}

// К какой команде относится person-поле. Пусто — роль не задана, предлагаются
// все участники.
function readPersonScopeTeamId(config: unknown): string {
  const raw = readRecord(readRecord(config)?.personScope);
  return typeof raw?.teamId === "string" ? raw.teamId : "";
}
